/**
 * Раздел «Дубли SN»: совпадающие серийные номера по всей сети.
 *
 * Совпадение серийного номера означает ошибку в данных: либо номер
 * записан не тому коммутатору, либо одно устройство заведено дважды.
 */
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { selectSwitches, selectPrefixMap } from '../../store/slices/switchesSlice';
import { findDuplicates, countDuplicates, DUPLICATE_FIELDS } from '../../domain/duplicates';
import { objectLabel } from '../../domain/issues';
import { plural } from '../../domain/plural';
import PageHeader from '../../components/common/PageHeader';
import styles from './DuplicatesPage.module.css';

export default function DuplicatesPage() {
  const rows = useSelector(selectSwitches);
  const prefixMap = useSelector(selectPrefixMap);
  const navigate = useNavigate();

  /* Пересчёт только при изменении списка: перебор полутора тысяч
     записей при каждой отрисовке был бы заметен на слабых машинах. */
  const duplicates = useMemo(() => findDuplicates(rows), [rows]);
  const total = countDuplicates(duplicates);

  const [fieldTab, setFieldTab] = useState(null);
  const active = fieldTab ?? DUPLICATE_FIELDS.find((f) => duplicates[f].length) ?? DUPLICATE_FIELDS[0];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Дубли серийных номеров"
        subtitle="Один серийный номер у нескольких коммутаторов — значит, где-то ошибка в учёте"
      />

      {total === 0 ? (
        <div className={styles.emptyOk}>
          <div className={styles.emptyTitle}>Совпадений нет</div>
          <p>Серийные номера во всех записях уникальны.</p>
        </div>
      ) : (
        <>
          <div className={styles.tabs} role="tablist">
            {DUPLICATE_FIELDS.map((field) => (
              <button
                key={field}
                role="tab"
                aria-selected={active === field}
                className={`${styles.tab} ${active === field ? styles.tabActive : ''}`}
                onClick={() => setFieldTab(field)}
              >
                {field}
                <span className={`${styles.count} ${duplicates[field].length ? styles.countErr : ''}`}>
                  {duplicates[field].length}
                </span>
              </button>
            ))}
          </div>
          <DuplicateSection
            field={active}
            groups={duplicates[active]}
            prefixMap={prefixMap}
            onOpen={(row) => navigate(`/objects/${row['Mag']}/${row['Коммутатор']}`)}
          />
        </>
      )}
    </div>
  );
}

function DuplicateSection({ field, groups, prefixMap, onOpen }) {
  if (!groups.length) {
    return <div className={styles.none}>По полю {field} совпадений нет.</div>;
  }

  return (
    <div className={styles.groups}>
      {groups.map(([serial, items]) => (
        <section className={styles.group} key={serial} aria-label={`Серийный номер ${serial}`}>
          <div className={styles.groupHeader}>
            <span className={styles.serial}>{serial}</span>
            <span className={styles.groupCount}>
              у {items.length} {plural(items.length, 'коммутатора', 'коммутаторов', 'коммутаторов')}
            </span>
          </div>
          <table className={styles.table}>
            <tbody>
              {items.map((row) => (
                <tr
                  key={`${row['Mag']}__${row['Коммутатор']}`}
                  onClick={() => onOpen(row)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpen(row); }}
                  tabIndex={0}
                >
                  <td className={styles.object}>{objectLabel(row['Mag'], prefixMap)}</td>
                  <td className={styles.mono}>{row['Коммутатор']}</td>
                  <td className={styles.mono}>{row['IP Коммутатора'] || '—'}</td>
                  <td className={styles.muted}>{row['Расположение'] || 'расположение не указано'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
